import React, { useEffect, useMemo, useState } from "react";
import { useDeleteDocument, useGetOrganizationDocuments } from "../api/organization";
import { Spin, Space, Input, message, Modal } from "antd";
import WithAuth from "../hoc/WithAuth";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";
import UploadDocumentModal from "./UploadDocuments";
import { AppButton } from "../components/ui/AppButton";
import { DataTable } from "../components/ui/data-table";
import { DocumentPreviewDialog } from "../components/modals/DocumentPreviewDialog";
import { Icons } from "../assets/images/icons";
import { Eye, Trash2 } from "lucide-react";

export enum DocumentType {
  TIN_CERTIFICATE = "TIN Certificate",
  BUSINESS_LICENSE = "Business License",
  OTHERS = "Others",
}

interface DocumentRecord {
  id: number;
  documentType: DocumentType;
  format: string;
  fileName: string;
  file?: any;
  documentUrl: string;
}

const DocumentsPage: React.FC = () => {
  const { user } = useAuth() as AuthContextValue;
  const {
    data: documents,
    isLoading,
    refetch,
  } = useGetOrganizationDocuments(user?.mfiId);
  const [open, setOpen] = useState(false);
  const { mutateAsync: deleteDocument, isPending:isDocumentPending} = useDeleteDocument()

  const hideModal = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [user, open]);

  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentRecord[]>(
    []
  );
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPdfPreview, setIsPdfPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [documentTitle, setDocumentTitle] = useState("");

  useEffect(() => {
    if (documents) {
      setUploadedDocuments(documents?.data?.data);
    }
  }, [documents]);

  const openPreview = (doc: any) => {
    const ext = doc?.name?.split(".").pop()?.toLowerCase();
    let mimeType = "";

    switch (ext) {
      case "pdf":
        mimeType = "application/pdf";
        break;
      case "jpg":
      case "jpeg":
        mimeType = "image/jpeg";
        break;
      case "png":
        mimeType = "image/png";
        break;
      default:
        console.error("Unsupported file type:", ext);
        return;
    }

    // Construct the correct data URL
    const fileUrl = `data:${mimeType};base64,${doc.file}`;
    setDocumentTitle(doc?.name);

    setPreviewUrl(fileUrl);
    setIsPdfPreview(mimeType === "application/pdf");
    setPreviewVisible(true);
  };

const handleDelete = (id: number) => {
  Modal.confirm({
    title: "Are you sure you want to delete this document?",
    onOk: () => {
      deleteDocument(id, {
        onSuccess: () => {
          message.success("Document deleted");
          refetch();
        },
        onError: () => message.error("Delete failed"),
      });
    },
  });
};


  const columns = [
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
      // width: 150,
    },
    {
      title: "Document Type",
      dataIndex: "documentType",
      key: "documentType",
      // width: 150,
      render: (_: any, record: any) => record.documentType ?? "N/A",
    },
    {
      title: "File Type",
      key: "type",
      // width: 150,
      render: (_: any, record: any) => {
        const name = record?.name;

        if (!name || !name.includes(".")) {
          return <span>N/A</span>; // no extension → N/A
        }

        const ext = name.split(".").pop()?.toLowerCase()?.trim();

        // If extension exists but is empty, still show N/A
        if (!ext) {
          return <span>N/A</span>;
        }

        return <span>{ext}</span>;
      },
    },

    {
      title: "Actions",
      key: "actions",
      // width: 250,
      render: (_: any, record: any) => {
        return (
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => openPreview(record)}
              className="text-green-600 hover:text-green-700"
              title="Preview"
            >
              <Eye size={18} />
            </button>

            <button
              onClick={() => handleDelete(record.id)}
              className="text-red-500 hover:text-red-600"
              title="Delete"
            >
              <Trash2 size={18}  />
            </button>
            
          </div>
        );
      },
    },
  ];

  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    let filtered = uploadedDocuments;

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter((item: any) =>
        item.name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  }, [uploadedDocuments, searchText]);

  const pageSize = 10;

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviewError = () => {
    setPreviewError(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setPreviewError(false);
  };

  return (
    <div
      className="bg-transparent lg:bg-white p-4 lg:p-6"
      style={{
        borderRadius: 8.5,
        marginTop: 25,
      }}
    >
      <div className="flex flex-col sm:flex-row justify-between mb-6">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search document by file name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            variant="success"
            onClick={() => setOpen(true)}
            customIcon={Icons.fileUpload}
          >
            Upload Document
          </AppButton>
        </div>
      </div>

      <Spin spinning={isLoading}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <UploadDocumentModal open={open} hideModal={hideModal} />

          {/* Uploaded documents table */}
          {uploadedDocuments.length > 0 && (
            <DataTable
              columns={columns}
              rowKey="id"
              data={paginatedData}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredData.length,
                onChange: handlePageChange,
              }}
              emptyText="No documents found"
            />
          )}

          <DocumentPreviewDialog
            open={previewVisible}
            onOpenChange={setPreviewVisible}
            title=""
            documentTitle={documentTitle.split(".")[0]}
            previewUrl={previewUrl}
            isPdfPreview={true}
          />
        </Space>
      </Spin>
    </div>
  );
};

export default WithAuth(DocumentsPage);
